import axios, { AxiosResponse } from 'axios'
import { getUsuarioByPessoaId } from 'app/modules/auth/core/_requests'
import {
  PageResponseUsuarioList,
  PasswordDtoChange,
  PasswordDtoReset,
  PerfilDtoUsuarioUpdate,
  UsuarioDtoCreateGrupo,
  UsuarioDtoGrupoUpdate,
  UsuarioDtoUpdate,
  UsuarioDtoVinculo,
  UsuarioView,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL
const USUARIO_URL = `${API_URL}/usuarios`
export const RESET_PASSWORD_URL = `${API_URL}/auth/reset-password`

/**
 * Ativa ou inativa um usuário
 * @param usuarioId Identificador do usuário
 * @param ativo status - boolean
 * @returns UsuarioView
 */
export const toggleStatusUsuario = (usuarioId: number, ativo: boolean): Promise<UsuarioView> => {
  return updateUser({ id: usuarioId, ativo })
}

/**
 * Busca todos os usuarios
 * @param query QueryState
 * @returns PageResponseUsuarioList
 */
export const getUsuarios = (query: string): Promise<PageResponseUsuarioList> => {
  return axios
    .get(`${USUARIO_URL}?${query}`)
    .then((d: AxiosResponse<PageResponseUsuarioList>) => d.data)
}

/**
 * Cadastra um usuário no grupo informado. A senha não vai no payload: o backend envia o e-mail de
 * confirmação da conta.
 * @param data UsuarioDtoCreateGrupo
 * @returns UsuarioView
 */
export const createUsuario = (data: UsuarioDtoCreateGrupo): Promise<UsuarioView> => {
  return axios.post(USUARIO_URL, data).then((resp: AxiosResponse<UsuarioView>) => resp.data)
}

/**
 * Busca um usuário
 * @param idUsuario ID do Usuario
 * @returns UsuarioView
 */
export const getUserById = (idUsuario: number): Promise<UsuarioView> => {
  return axios
    .get(`${USUARIO_URL}/${idUsuario}`)
    .then((response: AxiosResponse<UsuarioView>) => response.data)
}

/**
 * Busca os dados completos do usuário (incluindo dados da pessoa) para edição
 * @param usuarioId Identificador do usuário
 * @returns UsuarioDtoVinculo
 */
export const getUsuarioCompletoById = async (
  usuarioId: number
): Promise<UsuarioDtoVinculo | undefined> => {
  const usuario = await getUserById(usuarioId)
  if (!usuario?.pessoa?.id) return undefined
  return getUsuarioByPessoaId(usuario.pessoa.id)
}

/**
 * Altera senha do usuário autenticado
 * @param data PasswordDtoChange
 * @returns UsuarioView
 */
export const changePassword = (data: PasswordDtoChange): Promise<UsuarioView> => {
  return axios
    .put(`${USUARIO_URL}/password`, data)
    .then((resp: AxiosResponse<UsuarioView>) => resp.data)
}

export const resetPasswordUserConfirm = (data: PasswordDtoReset) => {
  return axios.post(RESET_PASSWORD_URL, data)
}

/**
 * Altera o usuário autenticado
 * @param usuario UsuarioDtoUpdate
 * @returns UsuarioView
 */
export const updateUserAutenticado = (usuario: UsuarioDtoUpdate): Promise<UsuarioView> => {
  return axios.put(`${USUARIO_URL}/`, usuario).then((resp: AxiosResponse<UsuarioView>) => resp.data)
}

/**
 * Altera o usuário autenticado
 * @param usuario UsuarioDtoUpdate
 * @returns UsuarioView
 */
export const updateUser = (usuario: UsuarioDtoUpdate): Promise<UsuarioView> => {
  return axios
    .put(`${USUARIO_URL}/${usuario.id}`, usuario)
    .then((resp: AxiosResponse<UsuarioView>) => resp.data)
}

/**
 * Remove o cadastro de um usuário
 * @param usuarioId Identificador do usuário
 * @returns void
 */
export const deleteUsuario = (usuarioId: number): Promise<void> => {
  return axios.delete(`${USUARIO_URL}/${usuarioId}`)
}

/**
 * Remove o segundo fator de um usuário: segredo do autenticador, códigos de recuperação e
 * qualquer cadastro em andamento. Saída para quem perdeu o celular e os códigos — no próximo
 * login, se a empresa exigir, ele refaz o cadastro do zero.
 * @param usuarioId Identificador do usuário
 * @returns void
 */
export const resetar2faUsuario = (usuarioId: number): Promise<void> => {
  return axios.delete(`${USUARIO_URL}/${usuarioId}/2fa`)
}

/**
 * Libera o login de um usuário bloqueado por excesso de tentativas inválidas (SBIS NGS1.02.13).
 * @param usuarioId Identificador do usuário
 * @returns void
 */
export const desbloquearUsuario = (usuarioId: number): Promise<void> => {
  return axios.patch(`${USUARIO_URL}/${usuarioId}/desbloqueio`)
}

/**
 * Altera o grupo/perfil de um usuário
 * @param usuarioId Identificador do usuário
 * @param data UsuarioDtoGrupoUpdate
 * @returns UsuarioView
 */
export const updateUsuarioGrupo = (
  usuarioId: number,
  data: UsuarioDtoGrupoUpdate
): Promise<UsuarioView> => {
  return axios
    .put(`${USUARIO_URL}/${usuarioId}/grupo`, data)
    .then((resp: AxiosResponse<UsuarioView>) => resp.data)
}

/**
 * Altera os perfis (permissões) de um usuário
 * @param usuarioId Identificador do usuário
 * @param data PerfilDtoUsuarioUpdate
 * @returns UsuarioView
 */
export const updateUsuarioPerfis = (
  usuarioId: number,
  data: PerfilDtoUsuarioUpdate
): Promise<UsuarioView> => {
  return axios
    .put(`${USUARIO_URL}/${usuarioId}/perfis`, data)
    .then((resp: AxiosResponse<UsuarioView>) => resp.data)
}
